import './CommercialWithApplicationId.css';

import { parse } from 'qs';
import React, { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Model,
  QuestionMatrixDynamicModel,
  QuestionSelectBase
} from 'survey-core';

import Loading from '../../components/Loading/Loading';
import {
  useGetApplicationGroupQuery,
  useGetHideEoQuery,
  useGetJsoNbyApplicationIdQuery
} from '../../generated/graphql';
import { Canada, US } from '../../utils/address/province';
import { UnderwritingUrlData } from '../../utils/getUnderwritingUrlData';
import {
  getPrimaryLobCoverageKey,
  getSiblingMunichCoverageKeys
} from '../../utils/lobCoverage';
import { stripFirstPageIdentityFields } from '../../utils/prefillAnswers';
import setMinMaxDateForEffectiveDate from '../../utils/setMinMaxDateForEffectiveDate';
import { getValueFromChoicesByLabel } from '../../utils/SurveyJSQuestion';
import { useFlags } from '../../utils/useFlags';
import CommercialSurveyWithApplicationId from './CommercialSurveyWithApplicationId';
import checkStateSupport from './supportedStates';
import { getAmtrustOfficerPanel } from './util';

interface CommercialWithApplicationIdParams {
  applicationGroupId: string;
}

interface CommercialNavState {
  entryApplicationId?: string;
  entryLobType?: string;
}

const CommercialWithApplicationId: React.FC = () => {
  const location = useLocation();
  const { group } = parse(location.search, {
    ignoreQueryPrefix: true
  });

  const { applicationGroupId } = useParams<CommercialWithApplicationIdParams>();

  // The URL carries an applicationGroupId. On the warm hop from Phase 1 the
  // entry (biz_coverage) application and its lobType arrive via router state, so
  // we skip the resolution query. On a cold load (refresh / pasted link / email)
  // we resolve from the URL id. getApplicationGroup's $or accepts either a
  // groupId or a legacy applicationId, so old /:applicationId links still work.
  const navState = location.state as CommercialNavState | null;
  const entryApplicationIdFromState = navState?.entryApplicationId;

  const {
    data: applicationGroupData,
    loading: isApplicationGroupLoading
  } = useGetApplicationGroupQuery({
    variables: { id: applicationGroupId },
    skip: !!entryApplicationIdFromState,
    errorPolicy: 'all'
  });

  // Entry app = applications[0] (group is created with [entry]; multi-LOB
  // siblings are $pushed after). Fall back to the URL id as an applicationId for
  // any group-less legacy app (defensive — post-migration all apps have a group).
  const entryApplication =
    applicationGroupData?.getApplicationGroup?.applications[0];
  const entryApplicationId =
    entryApplicationIdFromState ??
    entryApplication?.applicationId.toString() ??
    (isApplicationGroupLoading ? undefined : applicationGroupId);

  const {
    loading: isgetJSONbyApplicationIdLoading,
    error: getJSONbyApplicationIdError,
    data: getJSONbyApplicationIdData
  } = useGetJsoNbyApplicationIdQuery({
    variables: { applicationId: entryApplicationId ?? '' },
    skip: !entryApplicationId,
    // Refetch on mount so back-nav from Phase 3 gets fresh savedAnswers, not the
    // pre-selection cached first-load response.
    fetchPolicy: 'network-only'
  });

  // O(1) read of Configuration.hideEO; drives the EO Mock question's visibleIf.
  const { data: hideEOData, loading: isHideEOLoading } = useGetHideEoQuery();

  // Endorsements are scoped to a single LOB; the entry app's lobType drives the
  // sibling-coverage hide — from router state on the warm hop, else applications[0].
  const applicationLobType =
    navState?.entryLobType ?? entryApplication?.lobType;

  const { amTrustFullFlow } = useFlags();

  // Gate model setup on the queries it reads, plus the group resolution that
  // produces the entry applicationId.
  const canModelBeSetup =
    !!entryApplicationId && !!getJSONbyApplicationIdData && !!hideEOData;

  const model = useMemo(() => {
    if (!getJSONbyApplicationIdData) {
      return undefined;
    }

    const {
      getJSONbyApplicationId: {
        json,
        professionList,
        customerInfo,
        state,
        previousAnswers,
        savedAnswers,
        wcData,
        policyStartDateStr,
        carrierPartner,
        displayWcQuestions
      }
    } = getJSONbyApplicationIdData;

    const model = new Model(json);

    // set default value of province/state — only available on warm-load
    // (history.push from Phase 1). On cold-load, location.state is undefined
    // and we skip the default-set (model still loads from getJSONbyApplicationId).
    const { province } =
      (location.state as AddressParams | undefined) ?? ({} as AddressParams);
    const provinceValue = US.isLongName(province)
      ? US.getShortName(province)
      : Canada.isLongName(province)
      ? Canada.getShortName(province)
      : undefined;
    if (provinceValue) {
      model.setValue('BusinessInformation_100_BusinessAddress_WORLD_EN', {
        province: provinceValue
      });
    }

    const isStateSupported =
      US.isLongName(state) && checkStateSupport(US.getShortName(state));

    // TODO add helper function
    model.setVariable('isStateSupported', isStateSupported);
    model.setVariable('amTrustMaster', displayWcQuestions); // only internal brokers can bind Amtrust policy
    model.setVariable('amTrustFullFlow', amTrustFullFlow && displayWcQuestions);
    model.setVariable('isEndorsement', false);
    model.setVariable('isBindOnlineAmtrust', false);
    model.setVariable('carrierPartner', carrierPartner);
    model.setVariable('hideEO', hideEOData?.hideEO ?? true);

    const { firstName, lastName } = customerInfo;

    model.setValue(
      'BusinessInformation_100_Profession_WORLD_EN',
      professionList
    );
    model.setValue(
      'BusinessInformation_100_CustomerInfo_WORLD_EN',
      customerInfo
    );
    const currentYear = new Date().getFullYear();
    model.setVariable('currentYear', currentYear);

    const stateQuestion = model.getQuestionByName(
      'BusinessInformation_100_Locations_US_EN'
    );
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (stateQuestion) {
      stateQuestion.title = `${stateQuestion.title} ${state}?`;
    }

    const stateLocationQuestion = model.getQuestionByName(
      'BusinessInformation_100_State_WORLD_EN'
    );

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (state && stateLocationQuestion) {
      const stateValue = getValueFromChoicesByLabel(
        state,
        stateLocationQuestion as QuestionSelectBase
      );

      model.setValue('BusinessInformation_100_State_WORLD_EN', stateValue);
    }

    const effectiveDateQuestion = model.getQuestionByName(
      'BusinessInformation_100_EffectiveDate_WORLD_EN'
    );

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (effectiveDateQuestion && !effectiveDateQuestion.visible) {
      // set effective date
      model.setValue(
        'BusinessInformation_100_EffectiveDate_WORLD_EN',
        policyStartDateStr
      );
    }

    // endorsement case so wcData will not be defined
    if (previousAnswers) {
      return model;
    }

    if (!wcData) {
      throw new Error('wcData should be defined');
    }

    switch (wcData.__typename) {
      case 'GetJsonWcSuccess': {
        model.data = {
          ...model.data,
          Amtrust_Officers: [
            {
              name: `${firstName} ${lastName}`
            }
          ]
        };

        const { classCodes, rules, isContractor } = wcData;

        const amTrustOfficers = getAmtrustOfficerPanel(model);

        model.setVariable('rules', rules);
        model.setVariable('isContractor', isContractor);
        model.setVariable('amtrustAPIFail', false);

        if (US.isLongName(state)) {
          model.setVariable('UsState', US.getShortName(state));
        }

        const officerClassCode = amTrustOfficers.template.getQuestionByName(
          'duties'
        ) as QuestionMatrixDynamicModel;

        const employeePayroll = model.getQuestionByName(
          'Amtrust_EmployeePayrollMatrix_US_EN'
        ) as QuestionMatrixDynamicModel;

        const cleanedWcClassCodes = classCodes.map(
          ({ id: { __typename, ...id }, description }) => {
            void __typename; // strip __typename
            return {
              text: description,
              value: id
            };
          }
        );
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (officerClassCode && employeePayroll) {
          officerClassCode.choices = employeePayroll.choices = cleanedWcClassCodes;
        }

        break;
      }
      case 'GetJsonWcError': {
        // switch to broker flow

        const wcDyanmicQuestionPage = model.getPageByName(
          'AmtrustWcAdditionalQuestions'
        );

        const brokerflowPage = model.getPageByName(
          'BrokerWorkersCompensation_0001'
        );

        wcDyanmicQuestionPage.visible = false;
        brokerflowPage.visible = true;

        model.setVariable('amtrustAPIFail', true);

        break;
      }
      case 'GetJsonWcUnavailable': {
        // don't inject anything
        break;
      }
      default:
        throw new Error('Unreachable - unknown wcData response type'); // try updating your GrpahQL schema
    }

    // New-business back-nav: restore the user's biz_coverage edits (strip
    // identity fields set above). After wcData so the edits win over its defaults.
    if (savedAnswers) {
      model.setDataCore({
        ...model.data,
        ...stripFirstPageIdentityFields(savedAnswers, {
          keepEffectiveDate: true
        })
      });
      model.runTriggers();
    }

    return model;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryApplicationId, canModelBeSetup]); // note: this dependency array means we can only run this twice per application (i.e. before data is ready and after data is ready)

  if (
    !entryApplicationId ||
    isgetJSONbyApplicationIdLoading ||
    isHideEOLoading ||
    isApplicationGroupLoading
  ) {
    return <Loading />;
  }

  if (getJSONbyApplicationIdError || !getJSONbyApplicationIdData) {
    throw new Error(
      `${
        getJSONbyApplicationIdError
          ? 'getJSONbyApplicationId query error'
          : 'getJSONbyApplicationIdData should be ready'
      }`
    );
  }

  const {
    json,
    jsonFileName,
    previousAnswers,
    savedAnswers,
    policyStartDateStr,
    policyExpiryDateStr,
    professionList,
    transactionType
  } = getJSONbyApplicationIdData.getJSONbyApplicationId;

  if (!json) {
    throw new Error('JSON is not ready before render the form');
  }

  if (!professionList) {
    throw new Error('Could not find the profession');
  }

  if (typeof group !== 'string' && group !== undefined) {
    throw new Error('Unreachable - group type error');
  }

  if (!model) {
    throw new Error('Unreachable - model should be ready at this stage');
  }

  // for endorsement
  if (previousAnswers) {
    // TODO find a better way to set this flag
    model.setVariable('isEndorsement', true);

    model.setDataCore({
      ...model.data,
      ...stripFirstPageIdentityFields(previousAnswers)
    });

    // Layer the user's edits over the prior-policy base (strip first-page identity
    // fields) so back-nav restores them. Before coverage enforcement so the
    // single-LOB lock/hide still wins over any stale sibling selection.
    if (savedAnswers) {
      model.setDataCore({
        ...model.data,
        ...stripFirstPageIdentityFields(savedAnswers)
      });
    }

    // Endorsement = one LOB: unselect AND force-hide every non-endorsed Munich
    // coverage so Phase 2 shows and submits a single LOB. This covers GL's add-on
    // sub-coverages (Professional Liability, Limited Pollution, Terrorism, …), not
    // just the CGL/EO toggle — otherwise they leak onto an EO endorsement. Use
    // visibleIf='false' rather than visible=false — EOMock's own {hideEO}
    // visibleIf re-evaluates and would override a plain visible=false.
    if (applicationLobType) {
      const coverageOptionNames = model
        .getAllQuestions()
        .filter((q) => q.getType() === 'coverageoption')
        .map((q) => q.name);
      getSiblingMunichCoverageKeys(
        applicationLobType,
        coverageOptionNames
      ).forEach((name) => {
        const q = model.getQuestionByName(name);
        if (q) {
          model.setValue(name, { chooseCoverage: false });
          q.visibleIf = 'false';
        }
      });

      // Lock the endorsed LOB's own coverage toggle: the endorsement is based on
      // this coverage, so the user must not be able to deselect it. Keep it
      // checked (preserving its limit) and make the checkbox read-only.
      const primaryKey = getPrimaryLobCoverageKey(
        applicationLobType,
        coverageOptionNames
      );
      if (primaryKey) {
        const q = model.getQuestionByName(primaryKey);
        if (q) {
          const current = (model.getValue(primaryKey) ?? {}) as Record<
            string,
            unknown
          >;
          model.setValue(primaryKey, { ...current, chooseCoverage: true });
          q.readOnly = true;
        }
      }
    }
  }

  setMinMaxDateForEffectiveDate(
    model,
    policyStartDateStr,
    policyExpiryDateStr,
    transactionType
  );

  // for endorsement
  if (previousAnswers) {
    model.runTriggers();
  }

  interface AddressParams {
    country: string;
    province: string;
    brokerCode?: string;
  }
  const { country, province, brokerCode } =
    (location.state as AddressParams | undefined) ?? ({} as AddressParams);
  const urlData =
    (location.state as UnderwritingUrlData | undefined) ??
    ({} as UnderwritingUrlData);
  return (
    <div className="h-full Commercial" data-testid="Commercial">
      <CommercialSurveyWithApplicationId
        model={model}
        applicationId={entryApplicationId}
        jsonFileName={jsonFileName}
        groupName={group}
        country={country}
        province={province}
        urlData={urlData}
        brokerCode={brokerCode}
      />
    </div>
  );
};

export default CommercialWithApplicationId;
